import React, { useEffect, useState } from "react";
import { Table, Button, Space, Upload, Modal, message, Select, Divider, Popconfirm } from "antd";
import { DatabaseOutlined, DownloadOutlined, UploadOutlined, DeleteOutlined } from '@ant-design/icons';
import AdminPageLayout from "./layout/AdminPageLayout";
import useDumpStore from "../../stores/dumpStore";

const Dump = () => {
  const { dumps, loading, fetchDumps, createDump, downloadDump, restoreDatabase, deleteDump } = useDumpStore();
  const [restoreModalVisible, setRestoreModalVisible] = useState(false);
  const [fileToRestore, setFileToRestore] = useState(null);
  const [selectedDump, setSelectedDump] = useState(null);
  const [restoring, setRestoring] = useState(false);

  useEffect(() => {
    fetchDumps();
  }, [fetchDumps]);

  const columns = [
    {
      title: "Dump Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Date Created",
      dataIndex: "date",
      key: "date",
    },
    {
      title: "Size",
      dataIndex: "size",
      key: "size",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button 
            type="primary" 
            icon={<DownloadOutlined />}
            onClick={(e) => {
              e.preventDefault();
              downloadDump(record.name);
            }}
          >
            Download
          </Button>
          <Popconfirm
            title="Delete Dump"
            description="Are you sure you want to delete this dump?"
            onConfirm={() => deleteDump(record.name)}
            okText="Yes"
            cancelText="No"
          >
            <Button 
              type="primary" 
              danger
              icon={<DeleteOutlined />}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleRestore = async () => {
    if (!fileToRestore && !selectedDump) {
      message.error('Please select a file or existing dump to restore');
      return;
    }

    setRestoring(true);
    const result = await restoreDatabase(fileToRestore || selectedDump);
    setRestoring(false);

    if (result.success) {
      setRestoreModalVisible(false);
      setFileToRestore(null);
      setSelectedDump(null);
    }
  };

  const resetModal = () => {
    setRestoreModalVisible(false);
    setFileToRestore(null);
    setSelectedDump(null);
  };

  return (
    <AdminPageLayout
      title="Database Dumps"
      icon={<DatabaseOutlined />}
      subtitle="Manage database backups and dumps"
      listItems={[
        "View all database dumps",
        "Download database backups",
        "Create new database dumps",
        "Restore database from backup"
      ]}
      actions={
        <Space>
          <Button
            type="primary"
            icon={<DatabaseOutlined />}
            loading={loading}
            onClick={createDump}
          >
            Create New Dump
          </Button>
          <Button
            icon={<UploadOutlined />}
            onClick={() => setRestoreModalVisible(true)}
          >
            Restore Database
          </Button>
        </Space>
      }
    >
      <Table
        columns={columns}
        dataSource={dumps}
        pagination={false}
        loading={loading}
      />

      <Modal
        title="Restore Database"
        open={restoreModalVisible}
        onCancel={resetModal}
        onOk={handleRestore}
        okText="Restore"
        confirmLoading={restoring}
      >
        <p style={{ color: 'red', marginBottom: 24 }}>
          Warning: This will overwrite the current database. This action cannot be undone.
        </p>
        
        <div style={{ marginBottom: 16 }}>
          <h4>Select existing dump:</h4>
          <Select
            style={{ width: '100%' }}
            placeholder="Select a dump to restore"
            value={selectedDump}
            onChange={(value) => {
              setSelectedDump(value);
              setFileToRestore(null);
            }}
            options={dumps.map((dump) => ({
              label: `${dump.name} (${dump.date})`,
              value: dump.name,
            }))}
          />
        </div>

        <Divider>OR</Divider>

        <div>
          <h4>Upload new dump file:</h4>
          <Upload
            beforeUpload={(file) => {
              setFileToRestore(file);
              setSelectedDump(null);
              return false;
            }}
            maxCount={1}
            accept=".sql"
            onRemove={() => setFileToRestore(null)}
          >
            <Button icon={<UploadOutlined />}>Select Backup File</Button>
          </Upload>
        </div>
      </Modal>
    </AdminPageLayout>
  );
};

export default Dump;
